import { Button, Select, Typography } from 'antd';
import React, { Suspense, useMemo, useState } from 'react';
import { Payment, PaymentMethod, PAYMENT_METHOD_TEXT } from '~/types/Payment';
import { formatCurrency } from '~/utils/currency';
import SplitPaymentItem from './SplitPaymentItem';
import Loading from '~/App/components/Loading';
import ErrorBoundary from '~/App/ErrorBoundary';

const DEFAULT_OPTIONS = [
  PaymentMethod.Cash,
  PaymentMethod.EDC,
  PaymentMethod.Transfer,
  PaymentMethod.Wallet,
  PaymentMethod.Other,
];

const SplitPaymentTab = ({
  totalPrice,
  onSubmit,
}: {
  totalPrice: number;
  onSubmit: (params: Payment[]) => void;
}) => {
  const [control, setControl] = useState<PaymentMethod | null>(null);
  const [items, setItems] = useState<
    { method: PaymentMethod; amount: number; description: string }[]
  >([]);
  const [error, setError] = useState<string | null>(null);

  const onAdd = () => {
    if (!control) return;
    const newItems = [
      ...items,
      { method: control, amount: 0, description: '' },
    ];
    setItems(newItems);
    setControl(null);
  };

  const onChangeItemAmount = (value: number | null, index: number) => {
    items[index].amount = Math.max(0, value || 0);
    setItems([...items]);
  };
  const onChangeDescription = (value: string, index: number) => {
    items[index].description = value;
    setItems([...items]);
  };
  const remove = (index: number) => {
    items.splice(index, 1);
    setItems([...items]);
  };
  const onSelectChange = (value: PaymentMethod) => {
    setControl(value);
  };

  const options = useMemo(() => {
    const maps = items.reduce((acc, { method }) => {
      acc[method] = true;
      return acc;
    }, {} as Record<PaymentMethod, boolean>);
    return DEFAULT_OPTIONS.filter((val) => !maps[val]);
  }, [items]);

  const onOk = () => {
    setError(null);
    if (!items.length) return;

    if (items.some(({ amount }) => amount === 0)) {
      setError('Nominal tidak boleh ada yang 0');
      return;
    }

    if (
      items.some(
        ({ method, description }) =>
          method !== PaymentMethod.Cash && !description
      )
    ) {
      setError(`Semua deskripsi pembayaran harus diisi`);
      return;
    }

    onSubmit(items);
  };

  const totalPayment = items.reduce((acc, cur) => {
    acc += cur.amount;
    return acc;
  }, 0);

  return (
    <div>
      <div className="mb-[40px]">
        <Select
          value={control}
          dropdownMatchSelectWidth={true}
          onChange={onSelectChange}
          style={{ width: 300, marginRight: 8 }}
          placeholder="Pilih"
          size="large"
        >
          {options.map((val) => (
            <Select.Option key={val} value={val}>
              {PAYMENT_METHOD_TEXT[val]}
            </Select.Option>
          ))}
        </Select>
        <Button
          onClick={onAdd}
          disabled={control == null}
          type="primary"
          size="large"
        >
          Tambah
        </Button>
      </div>
      <div>
        {items.map(({ method, amount, description }, index) => {
          const onRemove = () => remove(index);
          const onChangeAmount = (value: number | null) =>
            onChangeItemAmount(value, index);
          const onChangeDesc = (value: string) =>
            onChangeDescription(value, index);
          return (
            <ErrorBoundary>
              <Suspense fallback={<Loading />}>
                <SplitPaymentItem
                  key={`${method}-${index}`}
                  method={method}
                  amount={amount}
                  description={description}
                  onChangeAmount={onChangeAmount}
                  onChangeDescription={onChangeDesc}
                  onRemove={onRemove}
                />
              </Suspense>
            </ErrorBoundary>
          );
        })}
      </div>
      {error ? <p style={{ color: 'red' }}>{error}</p> : null}
      <div className="row-between-center" style={{ marginBottom: 8 }}>
        <Typography.Text strong>Total</Typography.Text>
        <Typography.Text strong style={{ marginRight: 8 }}>
          {formatCurrency(totalPayment)}
        </Typography.Text>
      </div>
      <div className="row-between-center" style={{ marginBottom: 8 }}>
        <Typography.Text strong>Sisa Tagihan</Typography.Text>
        <Typography.Text strong style={{ marginRight: 8 }}>
          {formatCurrency(totalPrice - totalPayment)}
        </Typography.Text>
      </div>
      <div className="d-flex justify-content-end">
        <Button type="primary" disabled={!items.length} onClick={onOk}>
          OK
        </Button>
      </div>
    </div>
  );
};

export default SplitPaymentTab;
